import { useMemo } from 'react';
import { Reviews } from 'types/generated/client';
import { pluralize } from 'utils/shared/pluralize';
import { useModal } from 'hooks/useModal';
import Star from 'svg/Star';
import Button from 'components/Button';
import CardsSection from 'components/CardSection';
import NoDataPlaceholder from 'components/DiscoverPage/NoDataPlaceholder';
import ReviewCard from './ReviewCard';
import ReviewsOverviewModal from './ReviewsOverviewModal';

interface ReviewSectionProps {
  coachId: string;
  canCoachEditInfo?: boolean;
  reviews: Reviews[];
  loading?: boolean;
}

const ReviewSection: React.FC<ReviewSectionProps> = ({
  coachId,
  canCoachEditInfo = false,
  reviews = [],
  loading = false,
}) => {
  const reviewsOverviewModal = useModal();

  const withReviews = useMemo(() => reviews.filter((r) => r.review).length, [reviews]);

  const generalRating = useMemo(
    () =>
      reviews.length > 0
        ? (reviews.reduce((acc, review) => acc + review.rating, 0) / reviews.length).toFixed(1)
        : '0.0',
    [reviews],
  );

  // Hide section for players when there are no reviews and not loading
  const shouldShowSection = canCoachEditInfo || loading || reviews.length > 0;

  if (!shouldShowSection) {
    return null;
  }

  return (
    <>
      <div className="mt-ds-2xl border-t border-color-border-input-lightmode px-2 dark:border-color-border-input-darkmode xs:pt-ds-2xl lg:px-0  lg:pt-ds-3xl">
        <div className="typography-product-heading-compact-desktop flex items-center gap-2">
          <h3 className="typography-product-heading-desktop">
            {pluralize({
              singular: 'Review',
              plural: 'Reviews',
              count: reviews.length,
              hideCount: true,
            })}{' '}
            {reviews.length}
          </h3>
          {reviews.length > 0 && (
            <div className="flex items-center gap-1">
              <Star className="mb-1 h-6 w-6 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary" />
              <span className="">{generalRating}</span>
            </div>
          )}
        </div>
        {reviews.length > 0 && (
          <p className="typography-product-body mt-4 text-color-text-lightmode-secondary dark:text-color-bg-darkmode-secondary">
            {`${reviews.length} total ${pluralize({
              singular: 'rating',
              plural: 'ratings',
              count: reviews.length,
              hideCount: true,
            })}, ${withReviews} with ${pluralize({
              singular: 'review',
              plural: 'reviews',
              count: withReviews,
              hideCount: true,
            })}`}
          </p>
        )}
        {!loading && reviews.length === 0 && (
          <NoDataPlaceholder text="No reviews available" className="mt-6" />
        )}
        <CardsSection
          className="mt-6"
          gap="2rem"
          visibleCards={{ base: 1, sm: 1, md: 2 }}
          renderCard={(item) => <ReviewCard review={item} />}
          data={reviews}
        />
        {reviews.length > 0 && (
          <Button
            size="sm"
            variant="secondary"
            className="ml-auto mt-ds-xl w-fit"
            onClick={() => reviewsOverviewModal.openModal()}
          >
            Show all reviews
          </Button>
        )}
      </div>
      <ReviewsOverviewModal
        open={reviewsOverviewModal.isOpen}
        handleClose={reviewsOverviewModal.closeModal}
        reviews={reviews}
      />
    </>
  );
};

export default ReviewSection;
